/**
 * Type definitions for the qchess-api `/v1/*` namespace.
 *
 * QChess is the QLASH chess microservice: it lets QLASH users run chess
 * TOURNAMENTS (arena / swiss, hosted on Lichess) and 1v1 DUELS (also on
 * Lichess), and maintains a QLASH LEADERBOARD across them. Games are played on
 * Lichess; the Mini App embeds the Lichess board for viewing/replay and
 * deeplinks out to lichess.org for actual play (the fully-native Board API
 * client needs a backend proxy that does not exist yet — see qchessApi.ts).
 *
 * Hand-copied from the qchess-api contract (no codegen) — keep in sync when the
 * server contract changes. Endpoint base: `https://qchess.qlash.gg` (audience
 * `https://qchess.qlash.gg/api`).
 */

/* ── Lichess account link ─────────────────────────────────────────────── */

/**
 * Response body for `GET /v1/link` — the caller's current Lichess link.
 * `linked: false` is the "not linked yet" state (NOT an error). When linked,
 * `lichessUsername` is present and `scopes` lists the granted OAuth scopes.
 */
export interface LichessLinkStatus {
  linked: boolean;
  lichessUsername?: string;
  scopes?: string[];
  linkedAt?: string;
}

/**
 * Response body for `GET /v1/link/lichess/start` — the OAuth authorize URL to
 * open in the system browser (Telegram `openLink`). After the user consents,
 * the browser hits the qchess callback and the account is linked; the Mini App
 * then re-fetches `GET /v1/link` to pick up the linked state.
 */
export interface LichessLinkStartResponse {
  authorizeUrl: string;
}

/* ── Tournaments ──────────────────────────────────────────────────────── */

/** Tournament format on Lichess. */
export type TournamentKind = 'arena' | 'swiss';

/**
 * Lifecycle status of a chess tournament (mirrors qchess-api
 * `Tournament.status`). `created` → `started` (live on Lichess) → `finished`.
 */
export type TournamentStatus = 'created' | 'started' | 'finished';

/** A single row in a tournament's final standings. */
export interface TournamentStanding {
  rank: number;
  username: string;
  score: number;
}

/**
 * A chess tournament as returned by qchess-api (`POST /v1/tournaments` 201,
 * `GET /v1/tournaments` list items, `GET /v1/tournaments/:id`). The server
 * returns the document unwrapped (no `{ data }` envelope).
 */
export interface Tournament {
  lichessId: string;
  kind: TournamentKind;
  name: string;
  clockLimitSeconds: number;
  clockIncrementSeconds: number;
  variant: string;
  rated: boolean;
  status: TournamentStatus;
  lichessUrl: string;
  nbPlayers: number;
  finalStandings: TournamentStanding[];
  createdAt: string;
}

/** Response body for `GET /v1/tournaments` (no `{ data }` envelope). */
export interface ListTournamentsResponse {
  tournaments: Tournament[];
}

/** Query for `GET /v1/tournaments` (all optional). */
export interface ListTournamentsQuery {
  status?: TournamentStatus;
  page?: number;
  limit?: number;
}

/**
 * Request body for `POST /v1/tournaments`. `clockLimitSeconds` /
 * `clockIncrementSeconds` define the time control; `minutes` (arena) and
 * `nbRounds` (swiss) are kind-specific and optional. Returns the created
 * `Tournament` with HTTP 201.
 */
export interface CreateTournamentRequest {
  kind: TournamentKind;
  name?: string;
  clockLimitSeconds: number;
  clockIncrementSeconds: number;
  variant?: string;
  rated?: boolean;
  minutes?: number;
  nbRounds?: number;
}

/** Response body for `GET /v1/tournaments/:id/results`. */
export interface TournamentResultsResponse {
  results: TournamentStanding[];
}

/* ── Challenges (1v1 duels) ───────────────────────────────────────────── */

/** Best-of length for a chess duel. Defaults to `bo1` in the UI. */
export type ChessChallengeFormat = 'bo1' | 'bo3' | 'bo5';

/** Best-of options shown in the challenge picker (bo1 default). */
export const CHESS_CHALLENGE_FORMATS: readonly ChessChallengeFormat[] = [
  'bo1',
  'bo3',
  'bo5',
];

/**
 * Lifecycle status of a chess challenge (mirrors qchess-api
 * `Challenge.status`). `pending` → opponent accepts → `in_progress` (a Lichess
 * game is created; `currentGameId` is set) → `completed`. Other terminal
 * states: `declined`, `expired`, `aborted`.
 */
export type ChessChallengeStatus =
  | 'pending'
  | 'in_progress'
  | 'completed'
  | 'declined'
  | 'expired'
  | 'aborted';

/** Result projection on a completed/aborted challenge. */
export interface ChessChallengeResult {
  winnerLogtoUserId: string | null;
  reason: string;
}

/**
 * A chess challenge object as returned by qchess-api (`POST /v1/challenges`
 * 201, list items, and the accept/decline responses). The server returns the
 * full document unwrapped (no `{ data }` envelope).
 */
export interface Challenge {
  _id: string;
  challengerLogtoUserId: string;
  challengerUsername: string;
  opponentLogtoUserId: string;
  opponentUsername: string;
  format: ChessChallengeFormat;
  status: ChessChallengeStatus;
  winsChallenger: number;
  winsOpponent: number;
  currentGameId: string | null;
  /** Lichess game ids played in this duel, in order — used for the replay. */
  gameIds: string[];
  result: ChessChallengeResult | null;
  createdAt: string;
  /** Knockout integration: set when this challenge backs a bracket match. */
  origin?: 'duel' | 'tournament';
  bracketId?: string | null;
  bracketMatchId?: string | null;
}

/** Response body for `GET /v1/challenges` (no `{ data }` envelope). */
export interface ListChallengesResponse {
  challenges: Challenge[];
}

/** Query for `GET /v1/challenges` (status filter optional). */
export interface ListChessChallengesQuery {
  status?: ChessChallengeStatus;
}

/**
 * Request body for `POST /v1/challenges`. `opponentLogtoUserId` is the
 * opponent's canonical QLASH user id (Logto `sub`); the challenger is taken
 * from the Bearer JWT server-side. Returns the created challenge (HTTP 201).
 */
export interface CreateChessChallengeRequest {
  opponentLogtoUserId: string;
  format: ChessChallengeFormat;
  clockLimitSeconds: number;
  clockIncrementSeconds: number;
  variant?: string;
  rated?: boolean;
}

/* ── Leaderboard ──────────────────────────────────────────────────────── */

/** A single row in the QLASH chess leaderboard. */
export interface LeaderboardEntry {
  rank: number;
  logtoUserId: string;
  lichessUsername: string;
  points: number;
  tournamentsPlayed: number;
  bestRank: number | null;
  wins: number;
  losses: number;
  draws: number;
}

/** Response body for `GET /v1/leaderboard`. */
export interface LeaderboardResponse {
  seasonId: string;
  total: number;
  page: number;
  limit: number;
  entries: LeaderboardEntry[];
}

/** Query for `GET /v1/leaderboard` (paging optional). */
export interface LeaderboardQuery {
  page?: number;
  limit?: number;
}

/* ── Clock presets ────────────────────────────────────────────────────── */

/**
 * Curated time-control presets shown in the tournament/duel create forms.
 * Each carries the Lichess clock fields (`clockLimitSeconds` +
 * `clockIncrementSeconds`) plus an i18n category key for the label.
 */
export interface ClockPreset {
  /** Stable id used as the radio value + i18n suffix (chess.clock_<id>). */
  id: string;
  clockLimitSeconds: number;
  clockIncrementSeconds: number;
}

/** Bullet / Blitz / Rapid presets, in display order. */
export const CHESS_CLOCK_PRESETS: readonly ClockPreset[] = [
  { id: 'bullet', clockLimitSeconds: 60, clockIncrementSeconds: 0 },
  { id: 'blitz', clockLimitSeconds: 180, clockIncrementSeconds: 2 },
  { id: 'rapid', clockLimitSeconds: 600, clockIncrementSeconds: 0 },
];

/* ── Brackets (knockout tournaments) ──────────────────────────────────── */

/** Lifecycle of a knockout bracket (qchess-api `Bracket.status`). */
export type BracketStatus =
  | 'registration'
  | 'ready'
  | 'in_progress'
  | 'complete'
  | 'cancelled';

/** Seeding strategy chosen at creation (display-only in the TMA). */
export type BracketSeeding = 'random' | 'rating' | 'registration';

/** Per-match cell status. */
export type BracketSlotStatus = 'pending' | 'in_progress' | 'complete' | 'bye';

export interface BracketParticipant {
  logtoUserId: string;
  lichessUserId: string;
  lichessUsername: string;
  seed: number;
}

/**
 * One match cell of the bracket. `playerA`/`playerB` are a participant
 * `logtoUserId`, the literal `'BYE'`, or `null` (still awaiting a previous
 * round's winner). `challengeId` is the qchess Challenge that backs the match.
 */
export interface BracketSlot {
  roundIndex: number;
  slotIndex: number;
  playerA: string | null;
  playerB: string | null;
  challengeId: string | null;
  winner: string | null;
  status: BracketSlotStatus;
}

/**
 * A knockout bracket as returned by qchess-api (`GET /v1/brackets` items,
 * `GET /v1/brackets/:id`, and the join/leave/create/start/cancel responses).
 * Returned unwrapped (no `{ data }` envelope); `_id` is a string.
 */
export interface Bracket {
  _id: string;
  name: string;
  format: 'knockout';
  status: BracketStatus;
  createdBy: string;
  tenant: string;
  seasonId: string;
  bestOf: ChessChallengeFormat;
  clockLimitSeconds: number;
  clockIncrementSeconds: number;
  variant: string;
  rated: boolean;
  seeding: BracketSeeding;
  size: number;
  rounds: number;
  currentRound: number;
  participants: BracketParticipant[];
  /** bracket[roundIndex][slotIndex] */
  bracket: BracketSlot[][];
  registrationClosesAt: string | null;
  startsAt: string | null;
  completedAt: string | null;
  championLogtoUserId: string | null;
  finalStandings: TournamentStanding[];
  createdAt: string;
  updatedAt?: string;
}

/** Response body for `GET /v1/brackets`. */
export interface ListBracketsResponse {
  brackets: Bracket[];
}

/** Query for `GET /v1/brackets` (status filter optional). */
export interface ListBracketsQuery {
  status?: BracketStatus;
}

// Bracket creation/start/cancel (POST /admin/brackets*) is admin-only and
// handled by the qchess-backoffice, not the TMA — no request type is mirrored
// here on purpose.

/* ── Errors ───────────────────────────────────────────────────────────── */

/**
 * Error envelope for qchess 4xx responses: `{ "error": "<code>" }`. Mirrors
 * the qduels/qriots convention (bare `{ error }`, no `{ data }` wrapper).
 */
export interface QchessErrorBody {
  error: string;
}
